import React, { useRef, useCallback, useMemo, memo, useContext, useEffect } from 'react';

import { MAIN_TYPE } from '../../constants';
import { ErrorWithMetrics } from './error-utils';
import buildComponentsToRender from './build-components-to-render';
import debugPlugins from './debug-mode';
import joinDefinitions from './join-definitions';
import StaticPropsContext from '../../../../components/context/static-props';
import ComponentRenderer from '../../components/component-renderer';
import validatePlugin from './validate-plugin';

const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * withOctopus
 * @description Es un HOC que recibe una lista de plugins y retorna el componente con la lógica inyectada de los plugins que matcheen.
 * Un plugin proporciona las siguientes propiedades a los componentes definidos:
 *   - hocs: una lista de HOCs que se aplicarán al componente.
 *   - mappers: una lista de mappers que se aplicarán a los props del componente.
 *   - component: el componente que se renderizará.
 * @example   Implementación de ejemplo:
 * const enhance = compose(
 *   withFeatureFlag(FEATURE_NAME),
 *   connect(
 *     mapStateToProps,
 *     mapDispatchToProps,
 *   ),
 *   withOctopus(vpp, apparel, refurbished, mshops)({
 *     debugMode: true,
 *     namespace: 'vpp',
 *     featureName: "FEATURE_NAME",
 *   }),
 * );
 * @function withOctopus
 * @param {Array} plugins - Array of plugins
 * @returns {Function} - High order component
 * @param {Object} config - Config object
 * @returns {Function} - High order component
 * @param {Object} WrapperComponent - Wrapper component
 * @returns {Object} - High order component
 */

const withOctopus = ({ debugMode, componentName }, corePlugin, WrapperComponent = ComponentRenderer) => {
  const debug = IS_DEV && debugMode;

  corePlugin.id = 'core';
  corePlugin.isCore = true;

  const HighOrderComponent = memo(props => {
    const { plugins: restPlugins = [], namespace, featureName } = props || {};
    const componentsToRenderRef = useRef({});
    const staticProps = useContext(StaticPropsContext);
    const statics = useMemo(() => ({ ...staticProps, namespace, featureName }), [staticProps, namespace, featureName]);

    const handleError = useCallback((error, metrics) => {
      throw new ErrorWithMetrics(error, {
        ...metrics,
        featureName: `${featureName}_octopus`,
      });
    }, [featureName]);

    useEffect(() => {
      if (debug) {
        [corePlugin, ...restPlugins].forEach(plugin => validatePlugin(plugin));
      }
    }, [restPlugins]);

    const Main = useMemo(() => {
      const componentDefinitions = joinDefinitions([corePlugin, ...restPlugins], WrapperComponent, handleError);
      componentsToRenderRef.current = buildComponentsToRender(
        componentDefinitions,
        componentsToRenderRef,
        handleError,
        statics,
      );

      if (debug) {
        debugPlugins(restPlugins, componentDefinitions, featureName);
      }

      return componentsToRenderRef.current[MAIN_TYPE];
    }, [handleError, statics, restPlugins, featureName]);

    if (!props || !Main) {
      return null;
    }

    return <Main {...props} />;
  });

  HighOrderComponent.displayName = `withOctopus(Octopus${componentName})`;

  return HighOrderComponent;
};

export default withOctopus;
